import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useConfig, type IApp } from '@ohpen/react-utils';
import { useApiClient, UseApiClientOptions } from '@ohpen/react-auth';
import { useAuthz } from '@ohpen/react-authz';
import { PageSpinner, NotFound } from '@ohpen/enso-ui';

import * as pmpSdk from '@pmp/api';
import { AppLayout, ConfigHost, VersionBanner } from '@pmp/components';
import { AuthzProvider, HostChromeProvider, PmpConfigProvider } from '@pmp/contexts';
import { useAccessToken, useConfigAccess, useFeatureToggle } from '@pmp/hooks';
import { syncThemeControl } from '../AppLayout/themeControl';
import { Router } from '../../router';
import { registerAssistantAuth } from '../../assistant/agentTransport';
import { AssistantGate } from '../../assistant/AssistantGate';

/**
 * Configuration replaces the whole shell rather than rendering inside PMP's, because it
 * carries its own top bar and side bar and will one day be its own deployment.
 */
const AuthenticatedRoutes = (): ReactElement => {
  const { isLoading } = useAuthz();
  const { landingPath: configLandingPath } = useConfigAccess();
  const enableDarkMode = useFeatureToggle(['enableDarkMode']);

  // Runs after the shells' own theme effects, so it can remove a dark theme they leave behind.
  useEffect(() => {
    if (enableDarkMode === undefined) return;
    syncThemeControl(enableDarkMode, document.documentElement);
  }, [enableDarkMode]);

  // Unknown until the configuration and permissions endpoints answer. Registering the routes before
  // then makes the catch-all win, so a reload straight onto /config lands on PMP's not-found page.
  if (isLoading) return <PageSpinner />;

  return (
    <Routes>
      {configLandingPath && <Route path="config/*" element={<ConfigHost />} />}
      <Route
        path="*"
        element={
          <AppLayout>
            <Router />
          </AppLayout>
        }
      />
    </Routes>
  );
};

export const AuthenticatedContent = () => {
  const config = useConfig<IApp>();
  const getAccessToken = useAccessToken();
  const [showBanner, setShowBanner] = useState(false);
  const versionErrorShown = useRef(false);

  // Give the Assistant transport (which lives above AuthProvider) the session token getter.
  // The agent's URL is registered separately, in AssistantGate.tsx: it comes from the BFF's
  // /configuration/configuration response, which only PmpConfigProvider's subtree can read.
  useEffect(() => {
    registerAssistantAuth(getAccessToken);
  }, [getAccessToken]);

  const reload = useCallback(() => {
    if ('caches' in globalThis) {
      caches.keys().then(names => Promise.all(names.map(name => caches.delete(name))));
    }

    const url = new URL(globalThis.location.href);
    url.searchParams.set('_', Date.now().toString());
    globalThis.location.href = url.toString();
  }, []);

  const dismissBanner = useCallback(() => setShowBanner(false), []);

  const onVersionError = useCallback(() => {
    if (versionErrorShown.current) return;
    versionErrorShown.current = true;
    setShowBanner(true);
  }, []);

  const apiClientOptions: UseApiClientOptions = {
    config,
    version: config.VERSION,
    onVersionError,
    baseUrl: config?.APP?.apiBaseurl ?? '',
    client: pmpSdk.client,
    sdkModule: pmpSdk,
    getAccessToken,
  };

  const { isReady: apiClientReady, error: apiClientError } = useApiClient(apiClientOptions);

  if (!apiClientReady) {
    return <PageSpinner />;
  }

  if (apiClientError) {
    return <NotFound />;
  }

  return (
    <PmpConfigProvider getAccessToken={getAccessToken}>
      <AuthzProvider>
        <HostChromeProvider>
          <AssistantGate />
          <AuthenticatedRoutes />
        </HostChromeProvider>
      </AuthzProvider>
      {showBanner && <VersionBanner onReload={reload} onDismiss={dismissBanner} />}
    </PmpConfigProvider>
  );
};
